import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, CalendarDays, MessageCircle, TrendingUp } from "lucide-react";
import { athleteListPrograms, athleteMessagesUnreadCount } from "../../lib/api";

const POLL_MS = 60 * 1000;

/**
 * AthleteNav — quick-jump menu for the athlete portal.
 * Mobile: fixed bottom tab bar (like a native app).
 * Desktop: horizontal strip under the page header.
 * Items: Home · Schedule (their current program) · Messages (unread badge).
 */
export default function AthleteNav() {
  const location = useLocation();
  const [scheduleTarget, setScheduleTarget] = useState("/athlete/home");
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    athleteListPrograms()
      .then((progs) => {
        const sorted = [...(progs || [])].sort((a, b) =>
          (b.updated_at || "").localeCompare(a.updated_at || "")
        );
        if (sorted[0]?.id) setScheduleTarget(`/athlete/programs/${sorted[0].id}`);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const load = () =>
      athleteMessagesUnreadCount()
        .then((d) => setUnread(d.total || 0))
        .catch(() => {});
    load();
    const id = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [location.pathname]);

  const path = location.pathname;
  const items = [
    {
      key: "home",
      label: "Home",
      icon: Home,
      to: "/athlete/home",
      active: path === "/athlete/home",
    },
    {
      key: "progress",
      label: "Progress",
      icon: TrendingUp,
      to: "/athlete/progress",
      active: path === "/athlete/progress",
    },
    {
      key: "schedule",
      label: "Schedule",
      icon: CalendarDays,
      to: scheduleTarget,
      active: path.startsWith("/athlete/programs"),
    },
    {
      key: "messages",
      label: "Messages",
      icon: MessageCircle,
      to: "/athlete/messages",
      active: path === "/athlete/messages",
      badge: unread,
    },
  ];

  const renderItem = (it, variant) => {
    const Icon = it.icon;
    const activeCls = it.active
      ? "text-[var(--tir-burgundy)]"
      : "text-[var(--tir-muted)] hover:text-[var(--tir-ink)]";
    return (
      <Link
        key={it.key}
        to={it.to}
        data-testid={`athlete-nav-${it.key}${variant === "top" ? "-desktop" : ""}`}
        className={`relative flex ${
          variant === "bottom" ? "flex-col flex-1 py-2" : "flex-row gap-1.5 px-4 py-2.5"
        } items-center justify-center transition-colors ${activeCls}`}
      >
        <span className="relative">
          <Icon size={variant === "bottom" ? 20 : 14} strokeWidth={it.active ? 2.4 : 2} />
          {it.badge > 0 && (
            <span
              className="absolute -top-1.5 -right-2 min-w-[15px] h-[15px] px-0.5 bg-[var(--tir-burgundy)] text-white text-[8px] font-bold rounded-full flex items-center justify-center leading-none"
              data-testid={`athlete-nav-${it.key}-badge${variant === "top" ? "-desktop" : ""}`}
            >
              {it.badge > 9 ? "9+" : it.badge}
            </span>
          )}
        </span>
        <span
          className={`${
            variant === "bottom" ? "text-[9px] mt-1" : "text-[11px]"
          } tracking-[0.18em] uppercase font-semibold`}
        >
          {it.label}
        </span>
      </Link>
    );
  };

  return (
    <>
      {/* Desktop: strip under the header */}
      <nav
        className="hidden md:flex items-center justify-center border-b border-[var(--tir-border)] bg-white/90 backdrop-blur-sm sticky top-0 z-30"
        data-testid="athlete-nav-desktop"
      >
        {items.map((it) => renderItem(it, "top"))}
      </nav>
      {/* Mobile: fixed bottom tab bar */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 flex items-stretch bg-white border-t border-[var(--tir-border)] shadow-[0_-4px_16px_rgba(0,0,0,0.06)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="athlete-nav-bottom"
      >
        {items.map((it) => renderItem(it, "bottom"))}
      </nav>
    </>
  );
}
